import { useDebounce } from '@betterfunnel/react-hooks';
import { SectionBackgroundGradient, SectionData } from '@betterfunnel/types';
import Button from '@betterfunnel/ui-core/Button';
import IconButton from '@betterfunnel/ui-core/IconButton';
import Select from '@betterfunnel/ui-core/Select';
import Tabs, { Tab } from '@betterfunnel/ui-core/Tabs';
import Toggle from '@betterfunnel/ui-core/Toggle';
import ToggleButton from '@betterfunnel/ui-core/ToggleButton';
import { generatePalette } from '@betterfunnel/utils';
import { Transition } from '@headlessui/react';
import {
  ArrowDownIcon,
  ArrowDownLeftIcon,
  ArrowDownRightIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowRightStartOnRectangleIcon,
  ArrowUpIcon,
  ArrowUpLeftIcon,
  ArrowUpRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import * as Sentry from '@sentry/react';
import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { SketchPicker } from 'react-color';
import {
  DashboardContext,
  useDashboardContext,
} from '../contexts/DashboardContext';
import { classNames, distinct } from '../utils';
import FileUploadInput from './FormFields/FileUpload/FileUploadInput';
import { SectionStructureForm } from './SectionStructureForm/SectionStructureForm';

interface Props {
  open?: boolean;
  onClose: () => void;
  section?: SectionData;
  onChange: (_: SectionData) => void;
}
export default function EditorPopup({
  open,
  onClose,
  section,
  onChange: _onChange,
}: Props) {
  const [selectedTab, setSelectedTab] = useState('content');

  const tabs: Tab[] = useMemo(() => {
    const tabs = [
      { id: 'content', name: 'Content', current: false },
      { id: 'style', name: 'Style', current: false },
    ].map((tab) => ({
      ...tab,
      current: tab.id === selectedTab,
    }));

    return tabs;
  }, [selectedTab]);

  const [position, setPosition] = useState<'right' | 'left'>('right');
  const onChange = useCallback(
    (a: any) => {
      console.log('onChanged triggered');
      _onChange(a);
    },
    [_onChange],
  );

  return (
    <Transition
      show={open}
      as={Fragment}
      enter="ease-out duration-300"
      enterFrom="opacity-0 translate-x-4 sm:translate-x-0 sm:scale-95"
      enterTo="opacity-100 translate-y-0 sm:scale-100"
      leave="ease-in duration-200"
      leaveFrom="opacity-100 translate-x-0 sm:scale-100"
      leaveTo="opacity-0 translate-x-4 sm:translate-x-0 sm:scale-95"
    >
      <div
        className={classNames(
          'rounded-lg bg-white p-4 pt-0 shadow-2xl',
          'max-h-[90%] min-h-[50%]',
          'w-80',
          'absolute top-2',
          'overflow-x-hidden',
        )}
        style={{
          left: position === 'left' ? '0px' : undefined,
          right: position === 'right' ? '0px' : undefined,
        }}
      >
        <div className="sticky top-0 z-50 -mx-4 bg-white">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 pt-3 pb-2">
            <h3 className="text-base leading-6 font-semibold text-gray-900">
              Edit Section
            </h3>
            <div className="flex items-center gap-0">
              <IconButton
                variant="ternary"
                onClick={() =>
                  setPosition(position === 'right' ? 'left' : 'right')
                }
              >
                <ArrowRightStartOnRectangleIcon
                  className={classNames(
                    'h-5 w-5 text-gray-500',
                    position === 'right' ? 'rotate-180' : 'rotate-0',
                  )}
                  aria-hidden="true"
                />
              </IconButton>
              <IconButton variant="ternary" onClick={() => onClose()}>
                <XMarkIcon
                  className="h-5 w-5 text-gray-500"
                  aria-hidden="true"
                />
              </IconButton>
            </div>
          </div>
          <Tabs
            tabs={tabs}
            onTabClick={(id) => setSelectedTab(id)}
            fullWidth
            size="sm"
          />
        </div>

        <div key={section?.id} className="flex w-full flex-col gap-2 pt-4">
          {section ? (
            <>
              {selectedTab === 'content' ? (
                <ContentTab section={section} onChange={onChange} />
              ) : (
                <></>
              )}
              {selectedTab === 'style' ? (
                <StyleTab section={section} onChange={onChange} />
              ) : (
                <></>
              )}
            </>
          ) : (
            <></>
          )}
        </div>
      </div>
    </Transition>
  );
}

interface ContentTabProps {
  section: SectionData;
  onChange: (_: SectionData) => void;
}

const ContentTab = ({ section, onChange }: ContentTabProps) => {
  const { getSectionTypeConfig } = useDashboardContext();

  const sectionTypeConfig = useMemo(
    () => getSectionTypeConfig(section.type),
    [section.type, getSectionTypeConfig],
  );

  if (!sectionTypeConfig) {
    return (
      <div>
        <h1>Section Type Not Found</h1>
      </div>
    );
  }

  return (
    <SectionStructureForm
      content={section}
      fields={sectionTypeConfig.structure.fields}
      onChange={onChange}
      isSection
    />
  );
};

interface StyleTabProps {
  section: SectionData;
  onChange: (_: SectionData) => void;
}

export const StyleTab = ({ section, onChange }: StyleTabProps) => {
  const spacingOptions = useMemo(
    () => [
      {
        value: 'default',
        child: 'Default',
      },
      {
        value: 'none',
        child: 'None',
      },
      ...[
        2, 4, 6, 8, 12, 16, 20, 24, 32, 48, 56, 64, 72, 80, 96, 112, 128, 144,
        160, 176, 192, 208, 224, 240, 256,
      ].map((v) => ({
        value: v,
        child: `${v}px`,
      })),
    ],
    [],
  );
  const maxContentWidthOptions = useMemo(
    () => [
      {
        value: 'default' as const,
        child: 'Default',
      },
      {
        value: 0,
        child: 'None',
      },
      ...[576, 672, 768, 896, 1024, 1152, 1280, 1440, 1650, 1920].map((v) => ({
        value: v,
        child: `${v}px`,
      })),
    ],
    [],
  );

  return (
    <>
      <div>
        <label className="mb-2 block text-sm leading-6 font-medium text-gray-900">
          Background
        </label>

        <Select<'color' | 'gradient'>
          selected={section?.background?.type || 'color'}
          options={[
            { value: 'color', child: 'Color' },
            { value: 'gradient', child: 'Gradient' },
          ]}
          onChange={(selected) => {
            if (selected === section?.background?.type) return;

            if (selected === 'color') {
              onChange({
                ...section,
                background: null as any,
              });
            } else if (selected === 'gradient') {
              onChange({
                ...section,
                background: {
                  type: 'gradient',
                  colors: [
                    (section.background &&
                      'color' in section.background &&
                      section.background.color) ||
                      '#ccc',
                    '#fff',
                  ],
                  direction: 'to-right',
                },
              });
            }
          }}
        />
      </div>

      {section.background?.type !== 'gradient' ? (
        <ColorPickerField
          color={section.background?.color}
          onChange={(color) => {
            onChange({
              ...section,
              background: (color
                ? {
                    type: 'color',
                    color: color,
                  }
                : null) as any,
            });
          }}
        />
      ) : (
        <></>
      )}
      {section.background?.type === 'gradient' ? (
        <>
          <ColorPickerField
            disableDelete
            color={section.background?.colors?.[0]}
            onChange={(color) => {
              const background = section.background;
              if (!background || background.type !== 'gradient') return;

              onChange({
                ...section,
                background: {
                  ...background,
                  colors: [color, background.colors?.[1] || '#fff'],
                },
              });
            }}
          />

          <ColorPickerField
            disableDelete
            color={section.background?.colors?.[1]}
            onChange={(color) => {
              const background = section.background;
              if (!background || background.type !== 'gradient') return;

              onChange({
                ...section,
                background: {
                  ...background,
                  colors: [background.colors?.[0] || '#fff', color],
                },
              });
            }}
          />

          <div className="w-full" style={{ zoom: 0.8 }}>
            <ToggleButton<SectionBackgroundGradient['direction']>
              value={section.background?.direction ?? 'to-right'}
              items={[
                { value: 'to-top', icon: ArrowUpIcon },
                { value: 'to-top-right', icon: ArrowUpRightIcon },
                { value: 'to-right', icon: ArrowRightIcon },
                { value: 'to-bottom-right', icon: ArrowDownRightIcon },
                { value: 'to-bottom', icon: ArrowDownIcon },
                { value: 'to-bottom-left', icon: ArrowDownLeftIcon },
                { value: 'to-left', icon: ArrowLeftIcon },
                { value: 'to-top-left', icon: ArrowUpLeftIcon },
              ]}
              onChange={(selected) => {
                const background = section.background;
                if (!background || background.type !== 'gradient') return;

                onChange({
                  ...section,
                  background: {
                    ...background,
                    direction: selected,
                  },
                });
              }}
            />
          </div>
        </>
      ) : (
        <></>
      )}
      <ColorPickerField
        title="Text"
        color={section.textColor}
        onChange={(color) => {
          onChange({
            ...section,
            textColor: color as any,
          });
        }}
      />
      <div className="-mx-4 mt-2 border border-gray-200" />

      <div className="mt-2 flex flex-row items-baseline justify-between">
        <label className="block text-sm leading-6 font-medium text-gray-900">
          Background Image
        </label>
        <Toggle
          checked={section.backgroundImage?.show ?? false}
          onChange={(checked) => {
            onChange({
              ...section,
              backgroundImage: { ...section.backgroundImage, show: checked },
            });
          }}
        />
      </div>
      {section.backgroundImage?.show ? (
        <FileUploadInput
          image={section?.backgroundImage?.image}
          onSelected={(image) => {
            onChange({
              ...section,
              backgroundImage: {
                image: (image || null) as any,
                show: section.backgroundImage?.show,
              },
            });
          }}
          showImageSettings
        />
      ) : (
        <></>
      )}

      <div className="-mx-4 mt-2 border border-gray-200" />
      <div>
        <label className="mb-2 block text-sm leading-6 font-medium text-gray-900">
          Spacing Top
        </label>

        <Select<string | number>
          selected={section?.spacing?.top || 'default'}
          options={spacingOptions}
          onChange={(selected) => {
            onChange({
              ...section,
              spacing: {
                ...section.spacing,
                top: (!selected || selected === 'default'
                  ? null
                  : selected) as any,
              },
            });
          }}
        />
      </div>
      <div>
        <label className="mb-2 block text-sm leading-6 font-medium text-gray-900">
          Spacing Bottom
        </label>

        <Select<string | number>
          selected={section?.spacing?.bottom || 'default'}
          options={spacingOptions}
          onChange={(selected) => {
            onChange({
              ...section,
              spacing: {
                ...section.spacing,
                bottom: (!selected || selected === 'default'
                  ? null
                  : selected) as any,
              },
            });
          }}
        />
      </div>

      <div>
        <label className="mb-2 block text-sm leading-6 font-medium text-gray-900">
          Max Content Width
        </label>

        <Select<'default' | number | string>
          selected={section?.maxContentWidth ?? 'default'}
          options={maxContentWidthOptions}
          onChange={(selected) => {
            onChange({
              ...section,
              maxContentWidth: (selected === 'default'
                ? null
                : selected) as any,
            });
          }}
        />
      </div>

      <div className="mt-2 flex flex-row items-center justify-between">
        <label className="block text-sm leading-6 font-medium text-gray-900">
          Align
        </label>

        <ToggleButton<SectionData['mainAlignment'] | 'default'>
          value={section?.mainAlignment ?? 'default'}
          size="x-small"
          items={[
            { value: 'default', title: 'Default' },
            { value: 'center', title: 'Center' },
            { value: 'start', title: 'Start' },
            { value: 'end', title: 'End' },
          ]}
          onChange={(selected) => {
            onChange({
              ...section,
              mainAlignment: (!selected || selected === 'default'
                ? null
                : selected) as any,
            });
          }}
        />
      </div>
      <div className="mt-2 flex flex-row items-center justify-between">
        <label className="block text-sm leading-6 font-medium text-gray-900">
          Min Height
        </label>

        <ToggleButton<SectionData['minHeight'] | 'default'>
          value={section?.minHeight ?? 'default'}
          size="x-small"
          items={[
            { value: 'default', title: 'Default' },
            { value: 'auto', title: 'Content' },
            { value: 'screen', title: 'Screen' },
          ]}
          onChange={(selected) => {
            onChange({
              ...section,
              minHeight: (!selected || selected === 'default'
                ? null
                : selected) as any,
            });
          }}
        />
      </div>

      {/* <div className="mt-2 flex flex-row justify-between items-center">
        <label className="block text-sm font-medium leading-6 text-gray-900">
          Divider
        </label>

        <ToggleButton<SectionData['divider']>
          value={section?.divider ?? 'none'}
          size="x-small"
          items={[
            { value: 'none', title: 'None' },
            { value: 'linear', title: 'Linear' },
            { value: 'curved', title: 'Curved' },
            { value: 'wave', title: 'Wave' },
          ]}
          onChange={(selected) => {
            onChange({
              ...section,
              divider: selected,
            });
          }}
        />
      </div> */}
    </>
  );
};

const ColorPickerField = ({
  title,
  color,
  disableDelete,
  onChange,
}:
  | {
      title?: string;
      color: string | undefined;
      disableDelete?: false;
      onChange: (color: string | null) => void;
    }
  | {
      title?: string;
      color: string | undefined;
      disableDelete: true;
      onChange: (color: string) => void;
    }) => {
  const { accountBrand } = useContext(DashboardContext);

  const [showColorPicker, setShowColorPicker] = useState(false);
  const [currentColor, setCurrentColor] = useState<string | null | undefined>(
    undefined,
  );
  const debouncedBackgroundColor = useDebounce(currentColor, 500);

  const getRecentColors = useCallback((): string[] => {
    try {
      const currentRecentColors = localStorage.getItem('recentColors');
      const recentColors = currentRecentColors
        ? JSON.parse(currentRecentColors)
        : [];
      return recentColors;
    } catch (e) {
      Sentry.captureException(e);
      console.error('failed to get recent colors', e);
      return [];
    }
  }, []);

  useEffect(() => {
    if (debouncedBackgroundColor === undefined) return;
    if (debouncedBackgroundColor === null) {
      if (!disableDelete) {
        onChange(null);
      }
      return;
    }

    const recentColors = getRecentColors();
    const newRecentColors = [
      ...distinct([debouncedBackgroundColor, ...recentColors]),
    ].slice(0, 5);
    localStorage.setItem('recentColors', JSON.stringify(newRecentColors));
    console.log('debouncedBackgroundColor', debouncedBackgroundColor);

    onChange(debouncedBackgroundColor);
  }, [debouncedBackgroundColor, disableDelete, getRecentColors, onChange]);

  const _currentColor = useMemo(() => {
    if (currentColor === null) return null;
    if (currentColor === undefined) {
      return color;
    }
    return currentColor;
  }, [currentColor, color]);

  const primaryColor = useMemo(
    () => accountBrand?.primaryColor,
    [accountBrand],
  );

  return (
    <div className="relative">
      {title && (
        <label className="mb-2 block text-sm leading-6 font-medium text-gray-900">
          {title}
        </label>
      )}

      <Button
        variant="secondary"
        className="relative h-9 w-full"
        onClick={() => {
          setShowColorPicker(!showColorPicker);
        }}
      >
        <div className="absolute left-0 h-9 w-9 p-px">
          <div
            className="h-full w-full rounded-s-md border-r border-r-gray-200 bg-gray-200"
            style={{
              backgroundColor: _currentColor ?? '#fff',
            }}
          />
        </div>
        <div className="w-full ps-10 text-start">
          {_currentColor ?? 'Default'}
        </div>
        {!disableDelete ? (
          <div className="absolute top-0.5 right-1 bottom-0.5 flex">
            <IconButton
              variant="ternary"
              size="small"
              color="negative"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setCurrentColor(null);
                setShowColorPicker(false);
              }}
            >
              <XMarkIcon className="h-4 w-4" />
            </IconButton>
          </div>
        ) : (
          <></>
        )}
      </Button>

      {showColorPicker ? (
        <div className="fixed z-50">
          <div
            className="fixed top-0 right-0 bottom-0 left-0 -z-10 h-screen w-screen bg-gray-900/1"
            onClick={() => {
              setShowColorPicker(false);
            }}
          />

          <SketchPicker
            color={currentColor ?? color ?? '#fff'}
            disableAlpha={false}
            presetColors={distinct([
              ...getRecentColors(),
              primaryColor ?? '#ffffff',
              ...generatePalette(primaryColor ?? '#ffffff'),
              '#000000',
              '#ffffff',
              'transparent',
            ])}
            onChange={(color) => {
              if (color.hex === 'transparent') {
                setCurrentColor('transparent');
              } else if (color.hsl.a !== undefined && color.hsl.a < 100) {
                setCurrentColor(
                  color.hex + Math.round(color.hsl.a * 255).toString(16),
                );
              } else {
                setCurrentColor(color.hex);
              }
            }}
          />
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};
